import { useParams, Link, useNavigate } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import CartSidebar from "@/components/CartSidebar";
import { getBlogBySlug, getRelatedPosts } from "@/lib/blogs";
import { Calendar, Clock, User, ArrowLeft, Share2, Tag } from "lucide-react";
import NotFound from "./NotFound";

const BlogDetail = () => {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const post = slug ? getBlogBySlug(slug) : undefined;

  if (!post) {
    return <NotFound />;
  }

  const relatedPosts = getRelatedPosts(post, 3);

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleDateString("en-US", { 
      year: "numeric", 
      month: "long", 
      day: "numeric" 
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        // User cancelled or error occurred
      }
    } else {
      // Fallback: copy to clipboard
      navigator.clipboard.writeText(window.location.href);
      alert("Link copied to clipboard!");
    }
  };

  return (
    <div className="min-h-screen">
      <Header />
      <CartSidebar />
      
      <main>
        {/* Hero Image */}
        <section className="relative pt-24 pb-12 overflow-hidden">
          <div className="absolute inset-0">
            <img
              src={post.image}
              alt={post.title}
              className="w-full h-[400px] md:h-[500px] object-cover"
            />
            <div className="absolute inset-0 bg-gradient-to-b from-foreground/80 via-foreground/60 to-foreground/80" />
          </div>
          
          <div className="relative z-10 container mx-auto px-4">
            <Button
              variant="ghost"
              onClick={() => navigate("/blogs")}
              className="mb-6 text-primary-foreground hover:text-primary-foreground hover:bg-primary-foreground/10"
            >
              <ArrowLeft className="w-4 h-4 mr-2" />
              Back to Blogs
            </Button>
            
            <div className="max-w-3xl">
              <Badge className="bg-primary text-primary-foreground mb-4">
                {post.category}
              </Badge>
              <h1 className="font-display text-4xl md:text-5xl font-bold text-primary-foreground mb-6">
                {post.title}
              </h1>
              <div className="flex items-center gap-6 text-primary-foreground/90 flex-wrap">
                <div className="flex items-center gap-2">
                  <User className="w-5 h-5" />
                  <span className="font-medium">{post.author.name}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Calendar className="w-5 h-5" />
                  <span>{formatDate(post.publishedAt)}</span>
                </div>
                <div className="flex items-center gap-2">
                  <Clock className="w-5 h-5" />
                  <span>{post.readTime} min read</span>
                </div>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleShare}
                  className="bg-primary-foreground/10 border-primary-foreground/20 text-primary-foreground hover:bg-primary-foreground/20"
                >
                  <Share2 className="w-4 h-4 mr-2" />
                  Share
                </Button>
              </div>
            </div>
          </div>
        </section>

        {/* Content */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <div className="max-w-4xl mx-auto">
              <Card className="border-0 shadow-elevated">
                <CardContent className="p-8 md:p-12">
                  {/* Tags */}
                  {post.tags.length > 0 && (
                    <div className="flex flex-wrap gap-2 mb-8 pb-8 border-b border-border">
                      <Tag className="w-4 h-4 text-muted-foreground self-center" />
                      {post.tags.map((tag) => (
                        <Badge key={tag} variant="outline">
                          {tag}
                        </Badge>
                      ))}
                    </div>
                  )}

                  {/* Blog Content */}
                  <div
                    className="prose prose-lg max-w-none
                      prose-headings:font-display prose-headings:text-foreground
                      prose-p:text-muted-foreground prose-p:leading-relaxed
                      prose-a:text-primary prose-a:no-underline hover:prose-a:underline
                      prose-strong:text-foreground prose-strong:font-semibold
                      prose-h2:text-3xl prose-h2:mt-8 prose-h2:mb-4
                      prose-h3:text-2xl prose-h3:mt-6 prose-h3:mb-3
                      prose-ul:list-disc prose-ul:pl-6
                      prose-ol:list-decimal prose-ol:pl-6
                      prose-li:text-muted-foreground
                      prose-img:rounded-lg prose-img:shadow-md
                      prose-blockquote:border-l-4 prose-blockquote:border-primary prose-blockquote:pl-4 prose-blockquote:italic"
                    dangerouslySetInnerHTML={{ __html: post.content }}
                  />

                  {/* Author Section */}
                  <div className="mt-12 pt-8 border-t border-border">
                    <div className="flex items-center gap-4">
                      <div className="w-16 h-16 rounded-full bg-primary/10 flex items-center justify-center">
                        <User className="w-8 h-8 text-primary" />
                      </div>
                      <div>
                        <h3 className="font-display text-xl font-semibold text-foreground mb-1">
                          {post.author.name}
                        </h3>
                        <p className="text-sm text-muted-foreground">
                          Travel Writer & Solo Travel Enthusiast
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          </div>
        </section>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="py-12 bg-gradient-sunset">
            <div className="container mx-auto px-4">
              <div className="max-w-6xl mx-auto">
                <h2 className="font-display text-3xl font-bold text-foreground mb-8">
                  Related Posts
                </h2>
                <div className="grid md:grid-cols-3 gap-6">
                  {relatedPosts.map((relatedPost) => (
                    <Card
                      key={relatedPost.id}
                      className="border-0 shadow-card hover:shadow-elevated transition-all duration-300 overflow-hidden group cursor-pointer"
                    >
                      <Link to={`/blogs/${relatedPost.slug}`}>
                        <div className="relative overflow-hidden">
                          <img
                            src={relatedPost.image}
                            alt={relatedPost.title}
                            className="w-full h-40 object-cover group-hover:scale-105 transition-transform duration-300"
                          />
                        </div>
                        <CardContent className="p-6">
                          <Badge variant="outline" className="mb-3 text-xs">
                            {relatedPost.category}
                          </Badge>
                          <h3 className="font-display text-lg font-semibold text-foreground mb-2 group-hover:text-primary transition-colors line-clamp-2">
                            {relatedPost.title}
                          </h3>
                          <p className="text-muted-foreground text-sm line-clamp-2 mb-4">
                            {relatedPost.excerpt}
                          </p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <Clock className="w-3 h-3" />
                            {relatedPost.readTime} min read
                          </div>
                        </CardContent>
                      </Link>
                    </Card>
                  ))}
                </div>
              </div>
            </div>
          </section>
        )}

        {/* Back to Blogs CTA */}
        <section className="py-12 bg-background">
          <div className="container mx-auto px-4">
            <Card className="border-0 shadow-elevated max-w-4xl mx-auto">
              <CardContent className="p-8 text-center">
                <h2 className="font-display text-2xl font-bold text-foreground mb-4">
                  Explore More Stories
                </h2>
                <p className="text-muted-foreground mb-6">
                  Discover more travel tips, destination guides, and inspiring stories from solo travelers.
                </p>
                <Button asChild size="lg">
                  <Link to="/blogs">
                    View All Blogs
                  </Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default BlogDetail;

